import { Dialog, Portal } from '@chakra-ui/react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

function useDialog(): DialogContextValue {
  return useContext(DialogContext);
}
export const GenericDialog = () => {
  const { isOpen, content, onCloseDialog: onClose } = useContext(DialogContext);

  return (
    <Dialog.Root
      open={isOpen}
      onExitComplete={onClose}
      size="cover"
      placement="center"
      motionPreset="slide-in-bottom"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Body>{content}</Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export const DialogContextProvider = ({ children }: { children: ReactNode }) => {
  const [context, setContext] = useState<{ isOpen: boolean; content: ReactElement | null }>({
    isOpen: false,
    content: null,
  });

  const onOpen = useCallback((passedContent: ReactElement) => {
    setContext({ isOpen: true, content: passedContent });
  }, []);

  const onClose = useCallback(() => {
    setContext({ isOpen: false, content: null });
  }, []);

  const contextValue = useMemo(
    () => ({ ...context, openDialog: onOpen, onCloseDialog: onClose }),
    [context, onOpen, onClose],
  );

  return <DialogContext.Provider value={contextValue}>{children}</DialogContext.Provider>;
};

const DialogContext = createContext<DialogContextValue>({
  isOpen: false,
  content: null,
  openDialog: () => {},
  onCloseDialog: () => {},
});

type DialogContextValue = {
  isOpen: boolean;
  content: ReactElement | null;
  openDialog: (content: ReactElement) => void;
  onCloseDialog: () => void;
};
